// Standalone Nav + Footer chrome extracted from _v06_softconsumer.jsx so
// they can be mounted on every interior page of the portal — not just
// the homepage. Visual treatment is byte-identical to the variant, with
// all dynamic copy bindings (t/ts/useDomainCopy) collapsed to their
// default strings. The only configurable input is `brand`.
import React, { useState, useEffect } from 'react';
import { Link } from '../_shared.jsx';

const theme = {
  bg: '#fff8ee', fg: '#3a2a1c', muted: '#7a6655',
  border: '#f0e4d0', accent: '#e07a3a', accentFg: '#ffffff',
  inputBg: '#ffffff', radius: 16,
  font: '"Nunito", system-ui, sans-serif',
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : true);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update); };
  }, []);
  return isMobile;
};

const HamburgerIcon = ({ open }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? <><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></> :
            <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
  </svg>
);

const navLinks = [
  { label: 'So funktioniert’s', href: '/so-funktioniert-es' },
  { label: 'Bewertungen', href: '/forum' },
  { label: 'Förderung', href: '/förderung-finder' },
  { label: 'Kontakt', href: '/kontakt' },
];

const formatTelHref = (p) => 'tel:' + (p || '').replace(/[^0-9+]/g, '');

const PhoneGlyph = ({ size = 14, color = '#fff' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.96.34 1.9.66 2.8a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.32 1.84.54 2.8.66a2 2 0 0 1 1.73 2.07Z"/>
  </svg>
);

export function V06SoftConsumerNav({ brand = '', phone, brandLogo = null }) {
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const brandLabel = brand || (typeof window !== 'undefined' ? window.location.hostname : '');
  const brandBadge = (brandLabel && brandLabel[0]) ? brandLabel[0].toUpperCase() : '·';
  const phoneDisplay = phone || null;
  return (
    <div style={{
      padding: isMobile ? '12px 16px' : '16px 36px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      background: theme.bg, color: theme.fg, fontFamily: theme.font,
      position: 'relative',
    }}>
      <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: 'inherit' }} aria-label={brandLabel + ' Startseite'}>
        {brandLogo
          ? <img src={brandLogo} alt={brandLabel + ' Logo'} width="120" height="32" style={{ height: 32, width: 'auto', maxWidth: 160, display: 'block' }} />
          : <div style={{ width: 32, height: 32, borderRadius: 16, background: theme.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 16 }}>{brandBadge}</div>
        }
        {!brandLogo && <div style={{ fontWeight: 800, fontSize: 18 }}>{brandLabel}</div>}
      </Link>
      {!isMobile && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 26 }}>
          <div style={{ display: 'flex', gap: 26, fontSize: 14, fontWeight: 600, color: theme.muted }}>
            {navLinks.map(l => <Link key={l.label} href={l.href} style={{ color: theme.muted, textDecoration: 'none' }}>{l.label}</Link>)}
          </div>
          {phoneDisplay && (
            <a href={formatTelHref(phoneDisplay)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '8px 16px', borderRadius: 999,
              background: theme.accent, color: '#fff',
              fontSize: 14, fontWeight: 800, textDecoration: 'none',
              boxShadow: '0 4px 12px rgba(224, 122, 58, 0.25)',
            }}>
              <PhoneGlyph size={14} color="#fff" />
              {phoneDisplay}
            </a>
          )}
        </div>
      )}
      {isMobile && (
        <button
          aria-label="Menü"
          onClick={() => setIsMenuOpen(o => !o)}
          style={{ background: 'transparent', border: 'none', color: theme.fg, cursor: 'pointer', padding: 4, display: 'flex' }}
        >
          <HamburgerIcon open={isMenuOpen} />
        </button>
      )}
      {isMobile && isMenuOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: theme.bg, borderBottom: `1px solid ${theme.border}`,
          padding: '16px', display: 'flex', flexDirection: 'column', gap: 14,
          zIndex: 50,
        }}>
          {navLinks.map(l => (
            <Link key={l.label} href={l.href} style={{ color: theme.fg, textDecoration: 'none', fontSize: 15, fontWeight: 600 }}>{l.label}</Link>
          ))}
          {phoneDisplay && (
            <a href={formatTelHref(phoneDisplay)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 16px', borderRadius: 999,
              background: theme.accent, color: '#fff',
              fontSize: 15, fontWeight: 800, textDecoration: 'none',
              alignSelf: 'flex-start',
            }}>
              <PhoneGlyph size={15} color="#fff" />
              {phoneDisplay}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export function V06SoftConsumerFooter({ brand = '', domain = null, phone, brandLogo = null }) {
  const isMobile = useIsMobile();
  const brandLabel = brand || (typeof window !== 'undefined' ? window.location.hostname : '');
  const brandBadge = (brandLabel && brandLabel[0]) ? brandLabel[0].toUpperCase() : '·';
  const contactEmail = `info@${domain?.name || (brand ? brand.toLowerCase() + '.de' : 'scienceworks.com')}`;
  const phoneDisplay = phone || null;
  return (
    <footer style={{ padding: isMobile ? '20px 12px 28px' : '32px 48px 48px', background: theme.bg, color: theme.fg, fontFamily: theme.font }}>
      <div style={{
        maxWidth: 1184, margin: '0 auto',
        borderRadius: 24, padding: isMobile ? '24px 20px' : '40px 48px',
        borderTop: `1px solid ${theme.border}`,
        background: '#fff8ee',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr auto 1fr',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 18 : 24,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {brandLogo
              ? <img src={brandLogo} alt={brandLabel + ' Logo'} width="120" height="32" style={{ height: 32, width: 'auto', maxWidth: 160, display: 'block' }} />
              : <>
                  <div style={{
                    width: 32, height: 32, borderRadius: 16,
                    background: theme.accent, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: 16,
                  }}>{brandBadge}</div>
                  <div style={{ fontWeight: 800, fontSize: 17, color: theme.fg }}>{brandLabel}</div>
                </>
            }
          </div>

          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 10 : 26,
            fontSize: isMobile ? 13 : 14,
            fontWeight: 600,
            color: theme.muted,
            justifyContent: 'center',
          }}>
            {[
              { label: 'Impressum', href: '/impressum' },
              { label: 'Datenschutz', href: '/datenschutz' },
              { label: 'AGB', href: '/agb' },
              { label: 'Widerruf', href: '/widerruf' },
              { label: 'Kontakt', href: '/kontakt' },
            ].map(l => (
              <Link key={l.label} href={l.href} style={{ cursor: 'pointer', color: theme.muted, textDecoration: 'none' }}>{l.label}</Link>
            ))}
          </div>

          <div style={{
            display: 'flex', flexDirection: 'column',
            gap: 8,
            alignItems: isMobile ? 'flex-start' : 'flex-end',
            fontSize: 14, fontWeight: 800, color: theme.accent,
          }}>
            {phoneDisplay && (
              <a href={formatTelHref(phoneDisplay)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 14px', borderRadius: 999,
                background: '#fff', color: theme.accent,
                border: `1.5px solid ${theme.accent}`,
                textDecoration: 'none', fontSize: 14, fontWeight: 800,
              }}>
                <PhoneGlyph size={14} color={theme.accent} />
                {phoneDisplay}
              </a>
            )}
            <a href={`mailto:${contactEmail}`} style={{ color: theme.accent, textDecoration: 'none' }}>{contactEmail}</a>
          </div>
        </div>

        <div style={{
          marginTop: 22, paddingTop: 18,
          borderTop: `1px solid ${theme.border}`,
          fontSize: 12, color: theme.muted, fontWeight: 500,
          textAlign: 'center',
        }}>
          {`© ${new Date().getFullYear()} ${brandLabel}`}
        </div>
      </div>
    </footer>
  );
}
